/**
 * Changes the person makes to how transactions are counted, kept apart from
 * the extracted rows so re-reading a statement never loses them.
 *
 * - `Corrections`: one transaction, by txn_key (account, date, amount and
 *   occurrence; see withTxnKeys) — a new category, or "one-off". Rows saved
 *   before txn_key existed are matched by txn_id.
 * - `Rules`: every transaction whose merchant or description contains `match`
 *   (optionally on one account) — now and in future statements.
 *
 * A correction beats a rule; a later rule beats an earlier one. "One-off"
 * marks real spending that should not count towards the monthly averages and
 * savings rate (a car bought outright). See ARCHITECTURE.md, Corrections.
 */
import { formatTimestamp } from './dates'
import { TILLER_SOURCE } from './duplicates'
import { accountKey } from './filing'
import type { Database, Row } from './google/sheets'

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())
const yes = (v: unknown) => ['yes', 'true', '1', 'y'].includes(str(v).toLowerCase())

export interface Rule {
  rule_id: string
  match: string
  /** Empty for any account. */
  account_id: string
  /** Empty to keep each transaction's own category. */
  category: string
  one_off: boolean
}

export function toRule(r: Row): Rule {
  return { rule_id: str(r.rule_id), match: str(r.match), account_id: str(r.account_id), category: str(r.category), one_off: yes(r.one_off) }
}

export function ruleMatches(rule: Rule, t: { merchant?: unknown; description?: unknown; account_id?: unknown }): boolean {
  if (!rule.match) return false
  if (rule.account_id && rule.account_id !== str(t.account_id)) return false
  return `${str(t.merchant)} ${str(t.description)}`.toLowerCase().includes(rule.match.toLowerCase())
}

/**
 * Each transaction with its counted category. Adds `extracted_category`,
 * `one_off` ('yes' or '') and `corrected_by` ('you', 'rule: <match>' or '').
 */
export function applyCorrections(transactions: Row[], corrections: Row[] = [], rules: Row[] = []): Row[] {
  const byKey = new Map(corrections.filter((c) => str(c.txn_key)).map((c) => [str(c.txn_key), c]))
  const byId = new Map(corrections.filter((c) => !str(c.txn_key)).map((c) => [str(c.txn_id), c]))
  const active = rules.map(toRule).filter((r) => r.match)
  return transactions.map((t) => {
    // Safe to apply twice: rows already corrected keep what they have.
    const out: Row = { ...t, extracted_category: t.extracted_category ?? t.category, corrected_by: t.corrected_by ?? '' }
    // A one-off the source marked (Tiller's car purchases) stays one: a rule's
    // unticked box means "not set", not "regular", so a rule can only add the
    // flag. Among rules the later one wins; a correction on the row decides last.
    let ruleOneOff = false
    for (const rule of active) {
      if (!ruleMatches(rule, t)) continue
      if (rule.category) out.category = rule.category
      ruleOneOff = rule.one_off
      out.corrected_by = `rule: ${rule.match}`
    }
    let oneOff = t.one_off === 'yes' || ruleOneOff
    const c = (str(t.txn_key) && byKey.get(str(t.txn_key))) || byId.get(str(t.txn_id))
    if (c) {
      if (str(c.category)) out.category = str(c.category)
      oneOff = yes(c.one_off)
      out.corrected_by = 'you'
      if (str(c.stored_key)) out.stored_key = str(c.stored_key)
    }
    out.one_off = oneOff ? 'yes' : ''
    return out
  })
}

export const isOneOff = (t: Row) => t.one_off === 'yes'

/** Days apart a Tiller row and the statement row that replaced it may be dated. */
export const CARRY_DAYS = 3

/** tiller.ts builds `tiller:<account>:<date>:<amount>:<row>` for a row with no Transaction ID: a sheet position, not an id. */
const POSITION_ID = /^tiller:.+:\d{4}-\d{2}-\d{2}:-?[\d.]+:\d+$/

/** A Tiller row with Tiller's own Transaction ID, which never changes (its key's occurrence number can). */
export const stableTillerId = (id: string) => id.startsWith('tiller:') && !POSITION_ID.test(id)

/** account|date|amount|occurrence, without the `#<tiller id>` a Tiller row's correction is stored with. */
const baseKey = (k: string) => k.split('#')[0]

/**
 * Points each correction at the transaction it belongs to now, at most one
 * correction per transaction. Each output correction's `txn_key` is its
 * transaction's key; `stored_key` is the key it is stored under when that
 * differs, so saving or undoing touches the stored row and no other.
 *
 * 1. A correction on a Tiller row with a Transaction ID finds that row by id.
 *    (Saved with its key plus `#<tiller id>`, so two same-day twins never
 *    share a stored key.)
 * 2. Every other correction finds its row by key; one saved before txn_key
 *    existed, by txn_id.
 * 3. A Tiller correction whose row is gone (a statement has replaced it) moves
 *    to the statement row with the same key if it is free, else to the
 *    statement row on the same account for the same amount dated within
 *    CARRY_DAYS: closest date, then earliest.
 * A correction that finds no row is left out.
 */
export function keyCorrections(corrections: Row[] = [], keyed: Row[]): Row[] {
  const byId = new Map(keyed.map((t) => [str(t.txn_id), t]))
  const byKey = new Map(keyed.map((t) => [str(t.txn_key), t]))
  const claimed = new Set<string>()
  const out = new Map<Row, Row>()
  const take = (c: Row, t: Row) => {
    const key = str(t.txn_key)
    claimed.add(key)
    const stored = str(c.txn_key)
    out.set(c, { ...c, txn_key: key, ...(stored && stored !== key ? { stored_key: stored } : {}) })
  }
  const stable = (c: Row) => stableTillerId(str(c.txn_id))

  // 1. Tiller rows by id.
  for (const c of corrections) {
    const t = stable(c) ? byId.get(str(c.txn_id)) : undefined
    if (t && !claimed.has(str(t.txn_key))) take(c, t)
  }
  // 2. Everything else by key, or by txn_id for rows saved before keys.
  for (const c of corrections) {
    if (out.has(c) || stable(c)) continue
    const t = str(c.txn_key) ? byKey.get(str(c.txn_key)) : byId.get(str(c.txn_id))
    if (t && !claimed.has(str(t.txn_key))) take(c, t)
  }
  // 3. Tiller corrections whose row is gone: the same key first, across all of them, then nearby.
  const statements = keyed.filter((t) => str(t.source_file_id) !== TILLER_SOURCE)
  const orphans = corrections.filter((c) => !out.has(c) && str(c.txn_id).startsWith('tiller:') && !byId.has(str(c.txn_id)))
  for (const c of orphans) {
    const t = byKey.get(baseKey(str(c.txn_key)))
    if (t && str(t.source_file_id) !== TILLER_SOURCE && !claimed.has(str(t.txn_key))) take(c, t)
  }
  for (const c of orphans) {
    if (out.has(c)) continue
    // The account id is everything before the last three parts.
    const parts = baseKey(str(c.txn_key)).split('|')
    if (parts.length < 4) continue
    const [date, amount] = parts.slice(-3, -1)
    const account = accountKey(parts.slice(0, -3).join('|'))
    const at = Date.parse(date)
    if (!Number.isFinite(at)) continue
    const near = statements
      .filter((t) => !claimed.has(str(t.txn_key)) && accountKey(str(t.account_id)) === account && Number(t.amount).toFixed(2) === amount)
      .map((t) => ({ t, days: Math.abs(Date.parse(str(t.date)) - at) / 86_400_000 }))
      .filter(({ days }) => days <= CARRY_DAYS)
      .sort((a, b) => a.days - b.days || str(a.t.date).localeCompare(str(b.t.date)))[0]
    if (near) take(c, near.t)
  }
  return corrections.flatMap((c) => (out.has(c) ? [out.get(c)!] : []))
}

export interface CorrectionTarget {
  txn_id: string
  txn_key: string
  /** The key this row's correction is stored under, when that isn't txn_key (see keyCorrections). */
  stored_key?: string
}

/**
 * Where a correction on this row is written. A Tiller row's own correction
 * stays where it is stored, or goes under its key plus `#<tiller id>`. Any
 * other row's goes under its own key; one carried to it from a Tiller row is
 * moved there.
 */
function placement(t: CorrectionTarget): { key: string; move?: string } {
  if (stableTillerId(t.txn_id)) return { key: t.stored_key || `${t.txn_key}#${t.txn_id}` }
  return { key: t.txn_key, move: t.stored_key && t.stored_key !== t.txn_key ? t.stored_key : undefined }
}

export async function saveCorrection(db: Database, t: CorrectionTarget, change: { category: string; one_off: boolean }): Promise<void> {
  const { key, move } = placement(t)
  await db.upsert('Corrections', 'txn_key', [
    { txn_key: key, txn_id: t.txn_id, category: change.category, one_off: change.one_off ? 'yes' : '', updated_at: formatTimestamp(new Date()) },
  ])
  // The carried copy would otherwise come back.
  if (move) await db.replaceWhere('Corrections', 'txn_key', move, [])
}

export async function clearCorrection(db: Database, t: CorrectionTarget): Promise<void> {
  const { key, move } = placement(t)
  await db.replaceWhere('Corrections', 'txn_key', key, [])
  if (move) await db.replaceWhere('Corrections', 'txn_key', move, [])
  // A row saved before txn_key existed.
  await db.replaceWhere('Corrections', 'txn_id', t.txn_id, [])
}

export async function saveRule(db: Database, rule: Omit<Rule, 'rule_id'>): Promise<void> {
  const now = new Date()
  await db.upsert('Rules', 'rule_id', [
    {
      rule_id: `rule-${now.getTime()}`,
      match: rule.match,
      account_id: rule.account_id,
      category: rule.category,
      one_off: rule.one_off ? 'yes' : '',
      created_at: formatTimestamp(now),
    },
  ])
}

export async function deleteRule(db: Database, rule_id: string): Promise<void> {
  await db.replaceWhere('Rules', 'rule_id', rule_id, [])
}
